/**
 * useTranslationProvider — Provide/Inject bridge for i18n in shared components.
 *
 * Shared components in `@tms-phoenix/ui-vue` must work in apps with full i18n
 * (tms-app-soho) **and** apps with no i18n at all (tms-app-product).
 *
 * ### How it works
 *
 * 1. The app with i18n calls `provideTranslation(t, locale)` once near the root.
 * 2. Shared components call `useSharedTranslation()` to get the injected `t`.
 * 3. Apps without i18n do nothing — the fallback identity function returns the
 *    English default value (second argument) or the raw key.
 *
 * ### Example — app root (tms-app-soho)
 *
 * ```ts
 * import { useTranslation } from 'i18next-vue';
 * import { provideTranslation } from '@tms-phoenix/ui-vue';
 * import { useLocale } from '@/shared/composables/useLocale';
 *
 * const { t } = useTranslation();
 * const { locale } = useLocale();
 * // Guard against i18next returning the key string for missing translations.
 * provideTranslation((key, defaultValue) => {
 *   const result = t(key);
 *   return result === key ? (defaultValue ?? key) : result;
 * }, locale);
 * ```
 *
 * ### Example — shared component
 *
 * ```ts
 * import { useSharedTranslation } from '../../composables/useTranslationProvider';
 *
 * const { t, locale } = useSharedTranslation();
 * const label = computed(() => {
 *   const _lng = locale.value; // reactivity trigger
 *   return t('common.name', 'Name');
 * });
 * ```
 *
 * @module @tms-phoenix/ui-vue
 */
import { inject, provide, ref, type InjectionKey, type Ref } from 'vue';

// ─── Public types ───────────────────────────────────────────────────────────

/**
 * A translation function compatible with i18next's TFunction.
 *
 * @param key          - The translation key (e.g. `'common.name'`).
 * @param defaultValue - Fallback text returned when the key is missing or no
 *                       i18n runtime is available.
 * @returns The translated (or default) string.
 */
export type TranslateFn = (key: string, defaultValue?: string) => string;

/** The context object shared via provide/inject. */
export type TranslationContext = {
  /** Translation function — resolves keys to translated strings. */
  t: TranslateFn;
  /** Reactive locale ref — used as a dependency trigger inside `computed()`. */
  locale: Ref<string>;
};

// ─── Injection key ──────────────────────────────────────────────────────────

export const TranslationKey: InjectionKey<TranslationContext> =
  Symbol('TranslationContext');

// ─── Identity fallback ─────────────────────────────────────────────────────

/** No-op translator — returns `defaultValue` when provided, otherwise the raw key. */
const identityT: TranslateFn = (key: string, defaultValue?: string) =>
  defaultValue ?? key;

// ─── Public API ─────────────────────────────────────────────────────────────

/**
 * Provide a translation function and reactive locale to all descendant
 * components.  Call this **once** near the application root (e.g. `App.vue`).
 *
 * @param t      - The app's translation function (e.g. i18next `t`).
 * @param locale - A reactive `Ref<string>` representing the current language.
 */
export function provideTranslation(t: TranslateFn, locale: Ref<string>): void {
  provide(TranslationKey, { t, locale });
}

/**
 * Consume the injected translation context inside a shared component.
 *
 * When no ancestor called `provideTranslation`, the returned `t` is an
 * identity function that returns the English default value, and `locale`
 * defaults to `'en'`.
 */
export function useSharedTranslation(): TranslationContext {
  return inject(TranslationKey, { t: identityT, locale: ref('en') });
}
